import { useContext, useEffect } from 'react';
import type { RefObject } from 'react';
import { UNSAFE_LocationContext, useLocation } from 'react-router';

/**
 * Al volver con «atrás» a una lista, sigue donde estaba: el scroll se
 * guarda por entrada del historial (la `key` de la ubicación, que es la
 * misma al volver) y se repone al montar. Si la lista aún está cargando,
 * se reintenta cada vez que crece, hasta que haya sitio para bajar.
 */

const posiciones = new Map<string, number>();
/** Tiempo máximo esperando a que la lista tenga alto suficiente. */
const ESPERA_MS = 8000;
/** Entradas guardadas como mucho (las más viejas se olvidan). */
const MAXIMO = 50;

export function guardarPosicion(clave: string, y: number) {
  posiciones.delete(clave);
  posiciones.set(clave, y);
  if (posiciones.size > MAXIMO) {
    const primera = posiciones.keys().next().value;
    if (primera != null) {
      posiciones.delete(primera);
    }
  }
}

export function posicionGuardada(clave: string): number | undefined {
  return posiciones.get(clave);
}

/**
 * Repone en `el` la posición guardada con `clave` y la va guardando al
 * moverse. Si aún no hay sitio para bajar (la lista se está pintando), lo
 * reintenta cada vez que cambia de alto, hasta llegar o hasta que la
 * persona toca. Devuelve la limpieza.
 */
function recordarEn(el: HTMLElement, clave: string): () => void {
  const objetivo = posicionGuardada(clave);
  let tocado = false;
  let observador: ResizeObserver | null = null;
  let hijos: MutationObserver | null = null;
  let tope = 0;
  const parar = () => {
    observador?.disconnect();
    observador = null;
    hijos?.disconnect();
    hijos = null;
    window.clearTimeout(tope);
  };
  const reponer = () => {
    if (tocado || objetivo == null) {
      parar();
      return;
    }
    el.scrollTop = objetivo;
    if (Math.abs(el.scrollTop - objetivo) < 2) {
      parar();
    }
  };
  if (objetivo != null) {
    reponer();
    if (objetivo > 0 && Math.abs(el.scrollTop - objetivo) >= 2) {
      const ro = new ResizeObserver(reponer);
      observador = ro;
      ro.observe(el);
      for (const hijo of Array.from(el.children)) {
        ro.observe(hijo);
      }
      // Los grupos que se pintan después (días de la lista) también cuentan.
      hijos = new MutationObserver(cambios => {
        for (const c of cambios) {
          for (const n of Array.from(c.addedNodes)) {
            if (n instanceof Element) {
              ro.observe(n);
            }
          }
        }
        reponer();
      });
      hijos.observe(el, { childList: true });
      tope = window.setTimeout(parar, ESPERA_MS);
    }
  }

  const alTocar = () => {
    tocado = true;
    parar();
  };
  const alMover = () => guardarPosicion(clave, el.scrollTop);
  el.addEventListener('scroll', alMover, { passive: true });
  el.addEventListener('touchstart', alTocar, { passive: true });
  el.addEventListener('wheel', alTocar, { passive: true });
  return () => {
    parar();
    el.removeEventListener('scroll', alMover);
    el.removeEventListener('touchstart', alTocar);
    el.removeEventListener('wheel', alTocar);
  };
}

/**
 * `obtener` devuelve el elemento que hace scroll (puede no existir aún).
 * `nombre` distingue dos listas en la misma pantalla.
 */
export function useRecordarScroll(
  ref: RefObject<HTMLElement | null>,
  obtener: (raiz: HTMLElement) => HTMLElement | null = raiz => raiz,
  nombre = 'lista',
) {
  const { key } = useLocation();
  const clave = `${key}:${nombre}`;

  useEffect(() => {
    const raiz = ref.current;
    const el = raiz ? obtener(raiz) : null;
    return el ? recordarEn(el, clave) : undefined;
    // `obtener` es una búsqueda en el DOM: no cambia lo que se recuerda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, ref]);
}

/**
 * Lo mismo para un elemento que puede aparecer más tarde (una lista que
 * se pinta cuando llegan los datos): se le pasa el nodo de un callback ref.
 */
export function useRecordarScrollDe(el: HTMLElement | null, nombre: string) {
  // Sin router (pruebas de componentes sueltos) no hay nada que recordar.
  const key = useContext(UNSAFE_LocationContext)?.location?.key;
  const clave = key ? `${key}:${nombre}` : null;
  useEffect(
    () => (el && clave ? recordarEn(el, clave) : undefined),
    [el, clave],
  );
}
